import {OS, resolveByOS, sortByOS, type Architecture, type OSItem} from '@github-ui/get-os'

export type OSAwareDownloadAction = {
  id: string
  platform: OS
  architecture?: Architecture
  label: string
  href: string
  openInNewTab?: boolean
}

export type NormalizedDownloadAction = OSItem & {
  action: OSAwareDownloadAction
}

export type ResolvedDownloadActions = {
  primary: NormalizedDownloadAction
  items: NormalizedDownloadAction[]
}

const SAFE_DOWNLOAD_PROTOCOLS = new Set(['http:', 'https:'])

function validateDownloadAction(action: OSAwareDownloadAction): void {
  const href = action.href.trim()

  if (!href) {
    throw new Error(`Invalid download action href for "${action.id}"`)
  }

  let protocol: string

  try {
    protocol = new URL(href, 'https://github.com/').protocol
  } catch {
    throw new Error(`Invalid download action href for "${action.id}"`)
  }

  if (!SAFE_DOWNLOAD_PROTOCOLS.has(protocol)) {
    throw new Error(`Unsafe download action href for "${action.id}"`)
  }
}

function normalizeDownloadAction(action: OSAwareDownloadAction): NormalizedDownloadAction {
  validateDownloadAction(action)

  return {
    platform: action.platform,
    arch: action.architecture,
    action,
  }
}

export function resolveDownloadActions(
  actions: readonly [OSAwareDownloadAction, ...OSAwareDownloadAction[]],
  resolvedOS: OS,
  resolvedArch?: Architecture,
): ResolvedDownloadActions {
  const seenIds = new Set<string>()
  const normalized = actions.reduce<NormalizedDownloadAction[]>((deduped, action) => {
    if (seenIds.has(action.id)) return deduped

    seenIds.add(action.id)
    deduped.push(normalizeDownloadAction(action))
    return deduped
  }, []) as [NormalizedDownloadAction, ...NormalizedDownloadAction[]]
  const [fallback] = normalized
  const primary = resolveByOS(normalized, resolvedOS, resolvedArch) ?? fallback
  const items = resolvedOS === OS.Unknown ? normalized : sortByOS(normalized, resolvedOS, resolvedArch)

  return {primary, items}
}
